import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Container,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import { getCurrentUser, getPlans, subscribeToPlan } from "../../services/subcriptions";
import { SelectChangeEvent } from "@mui/material/Select";
import moment from "moment";

// Definición corregida de PlanType
interface PlanType {
    id: number;
    name: string;
    description: string;
    price: number;
    billing_cycle: string;
    features?: string[];
}

const SubscriptionScreen: React.FC = () => {
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [plans, setPlans] = useState<PlanType[]>([]);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState<number | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<string>('no_card');

    useEffect(() => {
        const initialize = async () => {
            try {
                const [planData, userData] = await Promise.all([
                    getPlans(),
                    getCurrentUser()
                ]);
                const sortedPlans = planData.sort((a: PlanType, b: PlanType) => a.price - b.price);
                setPlans(sortedPlans);
                setUserId(userData.user.id);
            } catch (error) {
                console.error('Error al inicializar:', error);
            }
        };

        initialize();
    }, []);

    const getBillingText = (cycle: string) => {
        switch (cycle) {
            case 'monthly': return 'mes';
            case 'yearly': return 'año';
            case 'weekly': return 'semana';
            default: return cycle;
        }
    };

    const calculateEndDate = (billingCycle: string): string => {
        const startDate = moment();
        switch (billingCycle) {
            case 'weekly': return startDate.add(1, 'week').format('YYYY-MM-DD');
            case 'monthly': return startDate.add(1, 'month').format('YYYY-MM-DD');
            case 'yearly': return startDate.add(1, 'year').format('YYYY-MM-DD');
            default: return startDate.add(1, 'month').format('YYYY-MM-DD');
        }
    };

    const handlePaymentMethodChange = (event: SelectChangeEvent) => {
        setPaymentMethod(event.target.value);
    };

    const handlePlanSelect = (plan: PlanType) => {
        setSelectedPlanId(currentId => currentId === plan.id ? null : plan.id);
        if (Number(plan.price) === 0) {
            setPaymentMethod('no_card');
        } else {
            setPaymentMethod('');
        }
    };

    const handleSubscribe = async () => {
        if (!userId || !selectedPlanId) {
            alert('Por favor seleccione un plan');
            return;
        }

        const selectedPlanData = plans.find(p => p.id === selectedPlanId);
        if (!selectedPlanData) return;

        if (Number(selectedPlanData.price) > 0 && !paymentMethod) {
            alert('Por favor seleccione un método de pago para este plan.');
            return;
        }

        setLoading(true);
        try {
            // Casting a 'any' para billing_type para evitar error de tipado estricto con la interfaz existente
            const subscriptionData: any = {
                plan_id: selectedPlanData.id,
                billing_type: selectedPlanData.billing_cycle,
                payment_method: paymentMethod || 'no_card',
                user: userId,
                end_date: calculateEndDate(selectedPlanData.billing_cycle)
            };

            await subscribeToPlan(subscriptionData);
            alert('¡Suscripción realizada con éxito! Bienvenido.');
        } catch (error: any) {
            console.error(error);
            alert(error.response?.data?.message || 'Error al procesar la suscripción');
        } finally {
            setLoading(false);
        }
    };

    const renderDescription = (description: string) => {
        const lines = description.split('\n');
        return (
            <List dense>
                {lines.map((line, index) => {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) return null;

                    if (trimmedLine.startsWith('✅') || trimmedLine.startsWith('✔')) {
                        return (
                            <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={trimmedLine.replace(/^[✅✔]\s*/, '')} />
                            </ListItem>
                        );
                    }
                    if (trimmedLine.startsWith('🔹') || trimmedLine.startsWith('💎')) {
                        return (
                            <Typography key={index} variant="subtitle2" color="primary" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                                {trimmedLine.replace(/^[🔹💎]\s*/, '')}
                            </Typography>
                        );
                    }
                     if (trimmedLine.startsWith('🎁') || trimmedLine.startsWith('⚡')) {
                        return (
                             <Box key={index} sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, borderLeft: '4px solid #ed6c02' }}>
                                <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                     {trimmedLine}
                                </Typography>
                            </Box>
                        );
                    }
                    return (
                        <Typography key={index} variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                            {trimmedLine}
                        </Typography>
                    );
                })}
            </List>
        );
    };

    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    const isPaidPlanSelected = selectedPlan ? Number(selectedPlan.price) > 0 : false;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box textAlign="center" mb={5}>
                <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                    Elige tu Plan Ideal
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    Desde aficionados hasta expertos. Tenemos las herramientas para que ganes.
                </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
                {plans.map((plan) => {
                    const isSelected = selectedPlanId === plan.id;
                    const isPremium = Number(plan.price) > 0;
                    const isFree = Number(plan.price) === 0;

                    return (
                        <Grid item xs={12} md={4} key={plan.id} display="flex">
                            <Card
                                variant={isSelected ? "elevation" : "outlined"}
                                elevation={isSelected ? 8 : 1}
                                onClick={() => handlePlanSelect(plan)}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: "pointer",
                                    borderColor: isSelected ? "primary.main" : "grey.200",
                                    borderWidth: isSelected ? 2 : 1,
                                    position: 'relative',
                                    transition: 'all 0.3s ease',
                                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                    '&:hover': {
                                        boxShadow: 6,
                                        borderColor: "primary.light"
                                    }
                                }}
                            >
                                {isPremium && (
                                    <Chip 
                                        label="Especialista" 
                                        color="primary" 
                                        size="small" 
                                        icon={<StarIcon />}
                                        sx={{ position: 'absolute', top: 10, right: 10 }} 
                                    />
                                )}
                                
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                                        {plan.name}
                                    </Typography>
                                    <Box display="flex" alignItems="baseline" mb={2}>
                                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                                            ${Number(plan.price).toLocaleString('es-CL')}
                                        </Typography>
                                        <Typography variant="subtitle1" color="text.secondary" ml={1}>
                                            / {getBillingText(plan.billing_cycle)}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <Box>
                                        {renderDescription(plan.description)}
                                    </Box>
                                </CardContent>
                                
                                <Box p={2} pt={0}>
                                    <Button 
                                        variant={isSelected ? "contained" : "outlined"}
                                        fullWidth
                                        color={isPremium ? "primary" : "secondary"}
                                        size="large"
                                    >
                                        {isSelected ? 'Seleccionado' : (isFree ? 'Probar Gratis' : 'Elegir Plan')}
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {selectedPlanId && (
                <Box 
                    position="fixed" 
                    bottom={0} 
                    left={0} 
                    right={0} 
                    p={3} 
                    bgcolor="background.paper" 
                    boxShadow={24} 
                    zIndex={1000}
                    borderTop="1px solid #e0e0e0"
                >
                    <Container maxWidth="md">
                        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6">
                                    Has seleccionado: <b>{selectedPlan?.name}</b>
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12} md={6} display="flex" gap={2} alignItems="center">
                                {isPaidPlanSelected && (
                                    <FormControl sx={{ minWidth: 200 }} size="small">
                                        <InputLabel>Medio de Pago</InputLabel>
                                        <Select
                                            value={paymentMethod}
                                            onChange={handlePaymentMethodChange}
                                            label="Método de Pago"
                                        >
                                            <MenuItem value="credit_card">Tarjeta de Crédito</MenuItem>
                                            <MenuItem value="debit_card">Tarjeta de Débito</MenuItem>
                                            <MenuItem value="paypal">PayPal</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                                
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    onClick={handleSubscribe}
                                    disabled={loading || (isPaidPlanSelected && !paymentMethod)}
                                    sx={{ minWidth: 150 }}
                                >
                                    {loading ? 'Procesando...' : 'Confirmar'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            )}
            <Box height={100} />
        </Container>
    );
};

export default SubscriptionScreen;